import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Badge, CircularProgress } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { produce } from "immer";
import { useRequest } from "ahooks";
import { CopyToClipboard } from "react-copy-to-clipboard";

import styles from "./header_voox.style";
import route_map from "../config/route_map";
import { message } from "@/lib";
import { useUserInfo } from "@/model/useUserInfo";
import { usePageMode } from "@/model/usePageMode";
import cookie, { removeToken } from "@/utils/cookie";
import { getLangFromPath, getLangPathPrefix } from "@/utils/langPath";
import service from "@/services/userinfo";

// import LogoLight from "@/assets/logo_light.png";
import LogoDark from "@/assets/logo_dark.png";
import arrowDown from "@/assets/svg/arrowDown.svg";
import menuIcon from "@/assets/svg/menu.svg";
import downloadIcon from "@/assets/svg/header_download.svg";
import countryIcon from "@/assets/svg/header_language.svg";
import sunIcon from "@/assets/svg/header_sun.svg";
import nightIcon from "@/assets/svg/header_moon.svg";
import messageIcon from "@/assets/svg/header_message.svg";
import headerAssetsIcon from "@/assets/svg/header_assets.svg";
import headerOrdersIcon from "@/assets/svg/header_orders.svg";
import headerAvatarIcon from "@/assets/svg/header_avatar.svg";
import walletIcon from "@/assets/svg/wallet.svg";
import spotIcon from "@/assets/svg/spot.svg";
import accountIcon from "@/assets/svg/contract.svg";
import futuresIcon from "@/assets/svg/futures.svg";
import financeIcon from "@/assets/svg/deposit-treasure.svg";
import copyIcon from "@/assets/svg/copy.svg";
import logoutIcon from "@/assets/svg/header-logout.svg";
import feeIcon from "@/assets/svg/header-fee.svg";
import feeLevelIcon from "@/assets/svg/header-fee-l1.svg";
import couponIcon from "@/assets/svg/header-coupon.svg";
import inviteIcon from "@/assets/svg/header-invite.svg";
import bonusIcon from "@/assets/svg/bonus.svg";
import rightIcon from "@/assets/svg/right.svg";
import radioCheckIcon from "@/assets/svg/radio-check.svg";
// import c2cLightIcon from "@/assets/svg/quicktrade_light.svg";
import c2cDarkIcon from "@/assets/svg/quicktrade_night.svg";
// import p2pLightIcon from "@/assets/svg/C2C_light.svg";
import p2pDarkIcon from "@/assets/svg/C2C_night.svg";
import feesZh from "@/assets/0fees-zh.png";
import fees from "@/assets/0fees.png";

/*
 * HeaderRC — 站点全局头部组件
 * 根据用户登录状态、屏幕尺寸、品牌模式动态渲染不同布局
 */

/* --- 状态管理 --- */
const useStyles = makeStyles()(styles);

const initState = {
  message: [],
  menu_list: [],
  show_menu_list: [],
  public_info: {},
  stats_rate: {},
  langArr: [],
  rateArr: [],
};

const reducer = produce((draft, action = { payload: {}, type: "@@INIT" }) => {
  const { payload } = action;
  Object.keys(payload || {}).forEach((key) => {
    draft[key] = payload[key];
  });
}, initState);

let hoverTimer = null;

/* hoverTimer 用于延迟关闭 hover 弹窗，避免鼠标快速划过时闪烁 */

const Svg = ({ src, width = 24, height = 24, className = "" }) => (
  <svg data-src={src} width={width} height={height} className={className} />
);

const getHeaderDomain = (subdomain) => {
  if (
    typeof window === "undefined" ||
    window.location.hostname === "localhost"
  ) {
    return "";
  }

  const port = window.location.port;
  const domain = window.location.hostname.split(".");
  const rootDomain =
    domain.length <= 2
      ? domain.join(".")
      : [domain[domain.length - 2], domain[domain.length - 1]].join(".");

  return `${window.location.protocol}//${subdomain}.${rootDomain}${
    port ? ":" + port : ""
  }`;
};

const getHeaderUserLink = (path, subdomain = "www") => {
  const lang =
    (typeof localStorage !== "undefined" && localStorage.lang) ||
    getLangFromPath() ||
    "en_US";
  return `${getHeaderDomain(subdomain)}${getLangPathPrefix(lang)}${path}`;
};

function HeaderRC() {
  /* ========== Hooks 初始化 ========== */

  const intl = useIntl();
  const { classes, cx } = useStyles();
  const [state, dispatch] = useReducer(reducer, initState);
  const [activePopover, setActivePopover] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const { userinfo, userinfo_loading, logoutFn, clearUserInfo } = useUserInfo();
  const {
    bodySize,
    mode,
    lang,
    curr_rate,
    appdownload,
    changePageMode,
    changeLang,
    changeRate,
  } = usePageMode();

  const width =
    bodySize?.width ||
    (typeof window !== "undefined" ? window.innerWidth : 1440);
  const isDesktop = width >= 1024;
  const isPad = width >= 768 && width < 1024;
  const isMobile = width < 768;
  const isCo = typeof window !== "undefined" && !!window.voox_co;
  const isLoggedIn = !!userinfo?.id;
  const currentLang = lang || getLangFromPath() || "en_US";
  const [isInZeroFeeTime, setIsInZeroFeeTime] = useState(false);
  const [spotZeroFeeDate, setSpotZeroFeeDate] = useState(
    () => getCachedZeroFeeDate() || {},
  );
  const popoverRef = useRef(null);

  /* 国际化快捷函数 */
  const t = (id, defaultMessage = id, values) =>
    intl.formatMessage({ id, defaultMessage }, values);

  /* ========== API 请求 ========== */
  const { runAsync: runMessageNoread } = useRequest(service.message_noread, {
    manual: true,
  });
  const { runAsync: runStatsRate } = useRequest(service.getStatsRate, {
    manual: true,
  });
  const { runAsync: runPublicInfoV4 } = useRequest(service.public_info_v4, {
    manual: true,
  });
  const { runAsync: runIndexV5 } = useRequest(service.index_v5, {
    manual: true,
  });

  /* 从 sessionStorage 读取缓存的零手续费时间 */
  function getCachedZeroFeeDate() {
    try {
      const cache = sessionStorage.getItem("languageFooterList");
      if (!cache) return null;
      return JSON.parse(cache)?.data?.spot_zero_fee_date || null;
    } catch (error) {
      return null;
    }
  }

  /* popover 悬停控制：进入立即显示，离开 80ms 延迟隐藏 */
  const mouseEnter = (id) => {
    clearTimeout(hoverTimer);
    hoverTimer = null;
    setActivePopover(id);
  };

  const mouseLeave = () => {
    hoverTimer = setTimeout(() => {
      setActivePopover(null);
    }, 80);
  };

  /* 获取未读消息列表 */
  const getIndexConfig = async () => {
    try {
      const res = await runMessageNoread({ payload: {} });
      if (res.code == 0) {
        dispatch({
          payload: {
            message: res.data?.userMessageList || [],
          },
        });
      }
    } catch (error) {}
  };

  /* 获取汇率数据，韩国用户屏蔽 KRW */
  const getStatsRate = async () => {
    try {
      const res = await runStatsRate({ payload: {} });
      if (res.code == 0) {
        const rateData = { ...(res.data || {}) };
        if (!userinfo?.isShowKorean) {
          Object.keys(rateData).forEach((key) => {
            if (key.indexOf("KRW") > -1) {
              delete rateData[key];
            }
          });
        }
        dispatch({
          payload: {
            stats_rate: rateData,
          },
        });
      }
    } catch (error) {}
  };

  /* 获取公共信息（含零手续费活动时间） */
  const getPublicInfoV4 = async () => {
    try {
      const res = await runPublicInfoV4({ payload: {} });
      if (res.code == 0) {
        dispatch({
          payload: {
            public_info: res.data || {},
          },
        });
        if (res.data?.spot_zero_fee_date) {
          setSpotZeroFeeDate(res.data.spot_zero_fee_date);
        }
      }
    } catch (error) {}
  };

  /* 获取远程主导航菜单配置，按语言过滤并排序 */
  const getIndexV5Config = async () => {
    try {
      const res = await runIndexV5({ payload: {} });
      if (res.code == 0) {
        const path = window.location.href.match(/[a-z]+_[A-Z]+/);
        const matchedLang =
          (path && path.length ? path[0] : null) ||
          getLangFromPath() ||
          cookie.read("lan") ||
          "en_US";
        const menuList = (res.data?.menu_list || [])
          .filter((item) => item.langKey === matchedLang)
          .sort((a, b) => a.sort - b.sort)
          .map((item) => {
            const next = { ...item };
            if (next.templateBannerList?.length) {
              next.templateBannerList = [...next.templateBannerList].sort(
                (a, b) => a.sort - b.sort,
              );
            }
            return next;
          });
        dispatch({
          payload: {
            menu_list: menuList,
          },
        });
      }
    } catch (error) {}
  };

  /* 退出登录：调接口 → 删 token → 跳登录页 */
  const logout = async (event) => {
    event?.preventDefault?.();
    try {
      const res = await logoutFn.runAsync({ payload: {} });
      if (res.code != 0) {
        message.error(res.msg);
        return;
      }
    } catch (error) {
      message.error(error.message);
      return;
    }

    removeToken();
    clearUserInfo();
    window.location.replace(route_map.login);
  };

  /* 拼装各子域名完整 URL（www/otc/futures/activity） */
  const getHeaderLink = () => {
    const linkurl = state.public_info?.url || {};
    const local = getLangPathPrefix(currentLang);

    if (process.env.NODE_ENV === "development") {
      return {
        exUrl: "",
        otcUrl: "",
        coUrl: "",
        activityUrl: "",
      };
    }

    return {
      exUrl: linkurl.exUrl
        ? `${linkurl.exUrl}${local}`
        : getHeaderDomain("www") + local,
      otcUrl: linkurl.otcUrl
        ? `${linkurl.otcUrl}${local}`
        : getHeaderDomain("otc") + local,
      coUrl: linkurl.coUrl
        ? `${linkurl.coUrl}${local}`
        : getHeaderDomain("futures") + local,
      activityUrl: getHeaderDomain("activity") + local,
    };
  };

  /* 根据 menuType 将导航项的相对路径拼接为对应子域名的完整 URL */
  const resolveNavUrl = (item, headerLink) => {
    const url = item?.httpUrl || "";
    const type = item?.menuType ? item.menuType.toString() : "";

    if (url.indexOf("http") > -1) {
      return url;
    }

    if (item?.target === "black") {
      return url;
    }

    if (type === "2") {
      return `${headerLink.otcUrl}${url}`;
    }

    if (type === "3") {
      return `${headerLink.coUrl}${url}`;
    }

    return `${headerLink.exUrl}${url}`;
  };

  /* 远程菜单不可用时的默认导航列表 */
  const fallbackMenuList = useMemo(
    () => [
      {
        id: "otc",
        title: t("Buy Crypto", "Buy Crypto"),
        httpUrl: route_map.otc,
        childList: [
          {
            title: t("footer.creditCardPurchase", "Buy Crypto"),
            subtitle: t(
              "header.quicktrade.desc",
              "Quickly buy and sell virtual assets using a bank card",
            ),
            // imageUrl: mode === "light" ? c2cLightIcon : c2cDarkIcon,
            imageUrl: c2cDarkIcon,
            httpUrl: route_map.otc,
          },
          {
            title: t("header.C2C", "P2P Trading"),
            subtitle: t(
              "header.C2C.desc",
              "Simple operations, Quick transactions, Start your first trade",
            ),
            // imageUrl: mode === "light" ? p2pLightIcon : p2pDarkIcon,
            imageUrl: p2pDarkIcon,
            httpUrl: route_map.c2c,
          },
        ],
      },
      {
        id: "market",
        title: t("Market", "Markets"),
        httpUrl: route_map.market,
      },
      {
        id: "spot",
        title: t("Spot", "Spot"),
        httpUrl: route_map.trade,
        isSpot: 1,
      },
      {
        id: "futures",
        title: t("Futures", "Futures"),
        httpUrl: route_map.futures,
      },
      {
        id: "copyTrading",
        title: t("koltrade", "Copy Trading"),
        httpUrl: route_map.kol,
      },
      {
        id: "bonus",
        title: t("Bonus", "Bonus"),
        httpUrl: route_map.bonus,
      },
      {
        id: "agent",
        title: t("Agent", "Affiliates"),
        httpUrl: route_map.agent,
      },
    ],
    // [currentLang, mode],
    [currentLang],
  );

  const menuList = state.show_menu_list.length
    ? state.show_menu_list
    : fallbackMenuList;

  /* 钱包入口列表：总览/现货/合约/跟单/理财 */
  const walletList = useMemo(() => {
    const list = [
      {
        title: t("totalAssets", "Total Assets Overview"),
        link: route_map.totalAssets,
        icon: <Svg src={walletIcon} />,
      },
      {
        title: t("exchangeAccount", "Spot Account"),
        link: route_map.exchangeAccount,
        icon: <Svg src={spotIcon} />,
        badge: userinfo?.isNewUser,
      },
      {
        title: t("coAccount", "Futures Account"),
        link: route_map.coAccount,
        icon: <Svg src={futuresIcon} />,
      },
    ];

    if (userinfo?.id && !userinfo?.kolUid) {
      list.push({
        title: t("kolAssets", "Copy Trading Assets"),
        link: route_map.kolAssets,
        icon: <Svg src={futuresIcon} />,
      });
    }

    list.push({
      title: t("financeAssets", "Finance Account"),
      link: route_map.financeAssets,
      icon: <Svg src={financeIcon} />,
    });

    return list;
  }, [userinfo, currentLang]);

  /* 订单入口列表：现货/合约/理财 */
  const orderList = useMemo(
    () => [
      {
        title: t("exchangeOrder", "Spot Orders"),
        link: route_map.exchangeOrder,
        icon: <Svg src={spotIcon} />,
      },
      {
        title: t("coOrder", "Futures Orders"),
        link: route_map.coOrder,
        icon: <Svg src={futuresIcon} />,
      },
      {
        title: t("financeOrder", "Finance Orders"),
        link: route_map.financeOrder,
        icon: <Svg src={financeIcon} />,
      },
    ],
    [currentLang],
  );

  /* 用户菜单列表：账户管理/费率/卡券/奖励/邀请/任务 */
  const userList = useMemo(
    () => [
      {
        title: t("header.u.account", "Account Management"),
        link: getHeaderUserLink("/my/userManagement"),
        icon: <Svg src={accountIcon} />,
      },
      {
        title: t("header.u.fee", "My Fee Rate"),
        link: getHeaderUserLink("/myRate"),
        icon: <Svg src={feeIcon} />,
        subTitle: true,
      },
      {
        title: t("header.u.coupon", "Coupon Center"),
        link: getHeaderUserLink("/my/couponCenter"),
        icon: <Svg src={couponIcon} />,
      },
      {
        title: t("header.u.bonus", "My Bonuses"),
        link: getHeaderUserLink("/bonus/my-bonus", "activity"),
        icon: <Svg src={bonusIcon} />,
      },
      {
        title: t("header.u.invite", "Invite Friends"),
        link: getHeaderUserLink("/inviteFriends"),
        icon: <Svg src={inviteIcon} />,
      },
      {
        title: t("header.u.reward", "Reward Center"),
        link: getHeaderUserLink("/taskCenter"),
        icon: <Svg src={bonusIcon} />,
      },
    ],
    [currentLang, userinfo],
  );

  /* 组件挂载时拉取全部初始化数据 */
  useEffect(() => {
    getPublicInfoV4();
    getIndexConfig();
    getIndexV5Config();
    getStatsRate();
  }, []);

  /* isShowKorean 变化时重新获取汇率 */
  useEffect(() => {
    getStatsRate();
  }, [userinfo?.isShowKorean]);

  /* 每秒检查零手续费是否过期 */
  useEffect(() => {
    if (spotZeroFeeDate?.endTime) {
      const checkZeroFeeTime = () => {
        const now = Date.now() / 1000;
        setIsInZeroFeeTime(now <= Number(spotZeroFeeDate.endTime));
      };

      checkZeroFeeTime();
      const timer = setInterval(checkZeroFeeTime, 1000);
      return () => clearInterval(timer);
    }

    setIsInZeroFeeTime(false);
  }, [spotZeroFeeDate]);

  /* 根据接口数据生成语言列表和汇率列表 */
  useEffect(() => {
    const lan = state.public_info?.lan;
    if (!lan?.lanList) {
      return;
    }
    let list = [];
    let rates = [];
    if (state.public_info?.lan) {
      list = [...state.public_info.lan.lanList].filter(
        (item) => item.id !== "ko_KR" && item.id !== "zh_CN",
      );
    }

    if (state.stats_rate) {
      Object.keys(state.stats_rate).forEach((key) => {
        if (key !== "default_lan") {
          rates.push({
            langKey: key,
            langName: key,
            lang_coin: key,
            lang_logo: state.stats_rate[key]?.legal_coin,
          });
        }
      });
    }

    dispatch({
      payload: {
        langArr: list,
        rateArr: rates,
      },
    });
  }, [state.public_info?.lan, userinfo?.isShowKorean, state.stats_rate]);

  /* 远程菜单数据变化时解析各导航项的完整 URL */
  useEffect(() => {
    if (!state.menu_list.length) {
      return;
    }

    const headerLink = getHeaderLink();
    const formatList = state.menu_list.map((item) => {
      const nav = {
        ...item,
        httpUrl: resolveNavUrl(item, headerLink),
      };

      if (item.templateBannerList?.length) {
        nav.childList = item.templateBannerList.map((child) => ({
          ...child,
          httpUrl: resolveNavUrl(child, headerLink),
        }));
      }

      return nav;
    });

    dispatch({
      payload: {
        show_menu_list: formatList,
      },
    });
  }, [state.menu_list, state.public_info?.url, currentLang]);

  const copy = () => {
    message.success(t("rewardsCenter.copySuccess", "Copied"));
  };

  /* 通用 hover 弹窗容器：鼠标进入触发，离开后延迟关闭 */
  const renderHoverPopover = (id, trigger, content, align = "right") => (
    <div
      className={classes.popoverHost}
      onMouseEnter={() => mouseEnter(id)}
      onMouseLeave={mouseLeave}
      ref={id === "more" ? popoverRef : undefined}
    >
      {trigger(activePopover === id)}
      <div
        className={cx(
          classes.popoverContent,
          align === "left" ? classes.popoverLeft : "",
          activePopover === id ? classes.showPopover : "",
        )}
      >
        {content}
      </div>
    </div>
  );

  /* 提取导航项的子菜单列表，兼容 childList 和 templateBannerList 两种字段 */
  const getChildren = (item) =>
    item?.childList || item?.templateBannerList || [];

  /* 渲染 Logo：根据主题切换亮/暗版本 */
  const renderLogo = () => (
    <div className={classes.logo}>
      <a href={route_map.index}>
        {/* <img src={mode === "dark" ? LogoDark : LogoLight} alt="VOOX" /> */}
        <img src={LogoDark} alt="VOOX" />
      </a>
    </div>
  );

  /* 渲染有下拉子菜单的导航项 */
  const renderNavDropdown = (item, index) => {
    const children = getChildren(item);
    const id = `nav-${item.id || index}`;

    return renderHoverPopover(
      id,
      (open) => (
        <a
          href={item.httpUrl}
          className={cx(classes.navLink, open ? "is-open" : "")}
        >
          {item.title}
          <Svg src={arrowDown} width={14} height={14} className="more" />
        </a>
      ),
      <div className={classes.navMenu}>
        {children.map((child, childIndex) => {
          // const img =
          //   mode === "light"
          //     ? child?.imageUrl || ""
          //     : child?.logoNavDark || child?.imageUrl || "";
          const img = child?.logoNavDark || child?.imageUrl || "";

          return (
            <a
              href={child.httpUrl}
              className={classes.navMenuItem}
              key={`${child.title}${childIndex}`}
              target={child.target === "black" ? "_blank" : undefined}
              rel={child.target === "black" ? "noreferrer" : undefined}
            >
              <span className={classes.navMenuIcon}>
                {img ? <img src={img} alt="" /> : <Svg src={couponIcon} />}
              </span>
              <span className={classes.navMenuText}>
                <span className={classes.title}>{child.title}</span>
                {child.subtitle ? (
                  <span className={classes.subTitle}>{child.subtitle}</span>
                ) : (
                  ""
                )}
              </span>
            </a>
          );
        })}
      </div>,
      "left",
    );
  };

  /* 主导航栏：仅桌面端非子站时显示 */
  const renderMainNav = () => {
    if (!isDesktop || !menuList?.length || isCo) {
      return null;
    }

    return (
      <nav className={classes.nav}>
        {menuList.map((item, index) => {
          const children = getChildren(item);
          return (
            <div
              className={classes.navItem}
              key={item.id || `${item.title}${index}`}
            >
              {children.length ? (
                renderNavDropdown(item, index)
              ) : (
                <a href={item.httpUrl} className={classes.navLink}>
                  {item.title}
                  {Number(item.isSpot) && isInZeroFeeTime ? (
                    currentLang === "zh_CN" || currentLang === "zh_TW" ? (
                      <img
                        className={classes.fees}
                        width={44}
                        height={14}
                        src={feesZh}
                        alt=""
                      />
                    ) : (
                      <img
                        className={classes.fees}
                        width={40}
                        height={14}
                        src={fees}
                        alt=""
                      />
                    )
                  ) : (
                    ""
                  )}
                </a>
              )}
            </div>
          );
        })}
      </nav>
    );
  };

  /* 垂直分割线 */
  const renderDivider = () => <span className={classes.divider} />;

  /* 登录/注册按钮 */
  const renderAuthActions = ({ showLogin = true } = {}) => (
    <div className={classes.actionGroup}>
      {showLogin ? (
        <a href={route_map.login} className={classes.loginAction}>
          {t("Log in", "Log In")}
        </a>
      ) : (
        ""
      )}
      {!isCo ? (
        <Button
          color="primary"
          href={route_map.register}
          variant="contained"
          className={classes.registerBtn}
        >
          {t("Sign Up", "Sign Up")}
        </Button>
      ) : (
        ""
      )}
    </div>
  );

  /* App 下载二维码弹窗 */
  const renderDownloadMenu = () => (
    <div className={classes.downloadMenu}>
      <div className={classes.qrcode}>
        {appdownload?.app_page_url ? (
          <img src={appdownload.app_page_url} alt="Download App" />
        ) : (
          ""
        )}
        <p>{t("header.appDownLoad", "Scan to Download APP")}</p>
        <a href={route_map.download} target="_blank" rel="noreferrer">
          {t("header.moreload", "More Download Options")}
        </a>
      </div>
    </div>
  );

  /* App 下载图标按钮（hover 弹窗） */
  const renderDownloadAction = () =>
    renderHoverPopover(
      "download",
      () => (
        <button
          type="button"
          className={classes.iconButton}
          aria-label={t("header.appDownLoad", "Download App")}
        >
          <Svg src={downloadIcon} />
        </button>
      ),
      renderDownloadMenu(),
    );

  /* 主题切换按钮 */
  const renderThemeAction = () => (
    <button
      type="button"
      className={classes.iconButton}
      onClick={() => changePageMode(mode === "light" ? "dark" : "light")}
      aria-label={t("header.color", "Theme")}
    >
      <Svg src={mode === "light" ? nightIcon : sunIcon} />
    </button>
  );

  /* 语言 & 汇率切换面板 */
  const renderLanguageMenu = ({ compact = false } = {}) => (
    <div
      className={cx(classes.menu, compact ? classes.compactLanguageMenu : "")}
    >
      <div className={classes.langTit}>
        <div className="tit">{t("header.language", "Language")}</div>
        <div className="tit">{t("header.rate", "Exchange Rate")}</div>
      </div>
      <div className={classes.langCon}>
        <ul>
          {state.langArr.map((item) => (
            <li
              onClick={() => changeLang(item.id)}
              key={item.id}
              className={currentLang === item.id ? "select" : ""}
            >
              {item.name}
              {currentLang === item.id ? (
                <Svg src={radioCheckIcon} width={17} height={17} />
              ) : (
                ""
              )}
            </li>
          ))}
        </ul>
        <ul>
          {state.rateArr.map((item) => (
            <li
              onClick={() => changeRate(item.langKey)}
              key={item.langKey}
              className={curr_rate === item.langKey ? "select" : ""}
            >
              {item.lang_coin} - {item.lang_logo}
              {curr_rate === item.langKey ? (
                <Svg src={radioCheckIcon} width={17} height={17} />
              ) : (
                ""
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  /* 语言切换图标按钮（hover 弹窗） */
  const renderLanguageAction = () =>
    renderHoverPopover(
      "language",
      () => (
        <button
          type="button"
          className={classes.iconButton}
          aria-label={t("header.language", "Language")}
        >
          <Svg src={countryIcon} />
        </button>
      ),
      renderLanguageMenu(),
    );

  /* 钱包下拉菜单 */
  const renderWalletMenu = () => (
    <div className={classes.menu}>
      {walletList.map((item, index) => (
        <a
          href={item.link}
          className={cx(classes.menuItem, classes.menuItem2)}
          key={`wallet${index}`}
        >
          {item.icon ? <span className="icon">{item.icon}</span> : ""}
          <span className={classes.title}>
            <Badge
              classes={{ dot: classes.badgeDot }}
              color="primary"
              variant="dot"
              invisible={!item.badge}
            >
              {item.title}
            </Badge>
          </span>
        </a>
      ))}
    </div>
  );

  /* 钱包图标按钮（hover 弹窗，已登录才显示） */
  const renderWalletAction = () => {
    if (!isLoggedIn || !walletList.length) {
      return null;
    }

    return renderHoverPopover(
      "wallet",
      () => (
        <a href={walletList[0]?.link || "#"} className={classes.iconButton}>
          <Badge
            classes={{ dot: classes.badgeDot }}
            color="primary"
            variant="dot"
            invisible={!userinfo?.isNewUser}
          >
            <Svg src={headerAssetsIcon} />
          </Badge>
        </a>
      ),
      renderWalletMenu(),
    );
  };

  /* 订单下拉菜单 */
  const renderOrderMenu = () => (
    <div className={classes.menu}>
      {orderList.map((item, index) => (
        <a
          href={item.link}
          className={cx(classes.menuItem, classes.menuItem2)}
          key={`order${index}`}
        >
          {item.icon ? <span className="icon">{item.icon}</span> : ""}
          <span className={classes.title}>{item.title}</span>
        </a>
      ))}
    </div>
  );

  /* 订单图标按钮（hover 弹窗，已登录才显示） */
  const renderOrderAction = () => {
    if (!isLoggedIn || !orderList.length) {
      return null;
    }

    return renderHoverPopover(
      "order",
      () => (
        <a href={orderList[0]?.link || "#"} className={classes.iconButton}>
          <Svg src={headerOrdersIcon} />
        </a>
      ),
      renderOrderMenu(),
    );
  };

  /* 消息通知下拉菜单 */
  const renderMessageMenu = () => (
    <div className={classes.menu}>
      <ul className={classes.messageList}>
        {state.message.map((item, index) => (
          <li className={classes.messageItem} key={`message${index}`}>
            <a href={route_map.message}>{item.messageContent}</a>
          </li>
        ))}
      </ul>
      <div className={classes.messageMore}>
        <a href={route_map.message}>
          {t("more", "View More")}
          <Svg src={rightIcon} width={16} height={16} />
        </a>
      </div>
    </div>
  );

  /* 消息图标按钮（有未读时显示 hover 弹窗，无未读时直接跳转） */
  const renderMessageAction = () => {
    if (!isLoggedIn) {
      return null;
    }

    const content = (
      <Badge
        classes={{ dot: classes.badgeDot }}
        color="primary"
        variant="dot"
        invisible={!state.message.length}
      >
        <Svg src={messageIcon} />
      </Badge>
    );

    if (!state.message.length) {
      return (
        <a href={route_map.message} className={classes.iconButton}>
          {content}
        </a>
      );
    }

    return renderHoverPopover(
      "message",
      () => (
        <a href={route_map.message} className={classes.iconButton}>
          {content}
        </a>
      ),
      renderMessageMenu(),
    );
  };

  /* 用户下拉菜单：账户信息/UID复制/各功能入口/退出 */
  const renderUserMenu = () => (
    <div className={classes.menu}>
      <div className={classes.userHeader}>
        <Svg src={headerAvatarIcon} width={44} height={44} />
        <div className={classes.userinfo}>
          <h3>
            <a href={getHeaderUserLink("/my/userManagement")}>
              {userinfo.userAccount ||
                userinfo.email ||
                userinfo.mobileNumber ||
                "--"}
            </a>
          </h3>
          <p>
            UID:{userinfo.id}
            <CopyToClipboard text={userinfo.id} onCopy={copy}>
              <button type="button">
                <Svg src={copyIcon} width={14} height={14} />
              </button>
            </CopyToClipboard>
          </p>
        </div>
      </div>
      {userList.map((item, index) => (
        <a
          href={item.link}
          className={cx(classes.menuItem, classes.menuItem2, "user")}
          key={`user${index}`}
        >
          {item.icon ? <span className="icon">{item.icon}</span> : ""}
          <span className={classes.userMenuText}>
            <span className={classes.title}>{item.title}</span>
            {item.subTitle ? (
              <span className={classes.vip}>
                <Svg src={feeLevelIcon} width={16} height={16} />
                {userinfo?.levelName}
              </span>
            ) : (
              ""
            )}
          </span>
        </a>
      ))}
      <button
        type="button"
        className={cx(classes.menuItem, classes.menuItem2, "user")}
        onClick={logout}
      >
        <span className="icon">
          <Svg src={logoutIcon} />
        </span>
        <span className={classes.title}>{t("header.u.logout", "Logout")}</span>
      </button>
    </div>
  );

  /* 用户头像图标按钮（hover 弹窗，已登录才显示） */
  const renderUserAction = () => {
    if (!isLoggedIn) {
      return null;
    }

    return renderHoverPopover(
      "user",
      () => (
        <button
          type="button"
          className={cx(classes.iconButton, classes.avatarButton)}
          aria-label={t("header.u.account", "Account")}
        >
          <Svg src={headerAvatarIcon} />
        </button>
      ),
      renderUserMenu(),
    );
  };

  /* 更多面板中的导航项（含子菜单展开） */
  const renderMoreNavItem = (item, index) => {
    const children = getChildren(item);

    return (
      <div
        className={classes.moreNavGroup}
        key={item.id || `${item.title}${index}`}
      >
        <a
          href={item.httpUrl}
          className={classes.moreItem}
          onClick={() => setMoreOpen(false)}
        >
          <span>{item.title}</span>
          {children.length ? (
            <Svg src={arrowDown} width={14} height={14} className="more" />
          ) : (
            ""
          )}
        </a>
        {children.length ? (
          <div className={classes.moreSubList}>
            {children.map((child, childIndex) => (
              <a
                href={child.httpUrl}
                className={classes.moreSubItem}
                key={`${child.title}${childIndex}`}
                onClick={() => setMoreOpen(false)}
              >
                <span>{child.title}</span>
                {child.subtitle ? <small>{child.subtitle}</small> : ""}
              </a>
            ))}
          </div>
        ) : (
          ""
        )}
      </div>
    );
  };

  /* 更多面板中的链接分组（钱包/订单等） */
  const renderMoreLinkGroup = ({ title, icon, list }) => {
    if (!list?.length) {
      return null;
    }

    return (
      <div className={classes.moreNavGroup}>
        <a
          href={list[0]?.link || "#"}
          className={classes.moreItem}
          onClick={() => setMoreOpen(false)}
        >
          <span className={classes.moreItemTitle}>
            {icon}
            {title}
          </span>
        </a>
        <div className={classes.moreSubList}>
          {list.map((item, index) => (
            <a
              href={item.link}
              className={classes.moreSubItem}
              key={`${title}${index}`}
              onClick={() => setMoreOpen(false)}
            >
              <span>{item.title}</span>
            </a>
          ))}
        </div>
      </div>
    );
  };

  /* 更多面板中的快捷操作行 */
  const renderMoreActionRows = () => (
    <div className={classes.moreSection}>
      {isLoggedIn
        ? renderMoreLinkGroup({
            title: t("header.assets", "Assets"),
            icon: <Svg src={headerAssetsIcon} width={20} height={20} />,
            list: walletList,
          })
        : null}
      {isLoggedIn
        ? renderMoreLinkGroup({
            title: t("header.order", "Orders"),
            icon: <Svg src={headerOrdersIcon} width={20} height={20} />,
            list: orderList,
          })
        : null}
      <a
        href={route_map.download}
        className={classes.moreItem}
        onClick={() => setMoreOpen(false)}
      >
        <span className={classes.moreItemTitle}>
          <Svg src={downloadIcon} width={20} height={20} />
          {t("header.appDownLoad", "Download App")}
        </span>
      </a>
      {isLoggedIn ? (
        <a
          href={route_map.message}
          className={classes.moreItem}
          onClick={() => setMoreOpen(false)}
        >
          <span className={classes.moreItemTitle}>
            <Badge
              classes={{ dot: classes.badgeDot }}
              color="primary"
              variant="dot"
              invisible={!state.message.length}
            >
              <Svg src={messageIcon} width={20} height={20} />
            </Badge>
            {t("Notice", "Notice")}
          </span>
        </a>
      ) : (
        ""
      )}
      <button
        type="button"
        className={classes.moreItem}
        onClick={() => changePageMode(mode === "light" ? "dark" : "light")}
      >
        <span className={classes.moreItemTitle}>
          <Svg
            src={mode === "light" ? nightIcon : sunIcon}
            width={20}
            height={20}
          />
          {t("header.color", "Theme")}
        </span>
      </button>
    </div>
  );

  /* 更多面板完整内容：导航 + 快捷操作 + 语言切换 */
  const renderMoreContent = () => (
    <div className={classes.morePanel}>
      <div className={classes.moreSection}>
        {menuList.map((item, index) => renderMoreNavItem(item, index))}
      </div>
      {renderMoreActionRows()}
      <div className={classes.moreSection}>
        {renderLanguageMenu({ compact: true })}
      </div>
    </div>
  );

  /* 更多按钮：桌面端 hover 弹窗，移动端侧滑面板 */
  const renderMoreAction = () => {
    if (!isDesktop) {
      return (
        <button
          type="button"
          className={classes.iconButton}
          onClick={() => setMoreOpen(true)}
          aria-label={t("more", "More")}
        >
          <Svg src={menuIcon} />
        </button>
      );
    }

    return renderHoverPopover(
      "more",
      () => (
        <button
          type="button"
          className={classes.iconButton}
          aria-label={t("more", "More")}
        >
          <Svg src={menuIcon} />
        </button>
      ),
      renderMoreContent(),
    );
  };

  /* 桌面端右侧操作区：区分已登录/未登录两套布局 */
  const renderDesktopActions = () => {
    if (userinfo_loading) {
      return (
        <div className={classes.option}>
          <CircularProgress size={18} />
        </div>
      );
    }

    if (isLoggedIn) {
      return (
        <div className={classes.option}>
          <div className={classes.actionGroup}>
            {renderWalletAction()}
            {renderOrderAction()}
            {renderUserAction()}
          </div>
          {renderDivider()}
          <div className={classes.actionGroup}>
            {renderDownloadAction()}
            {renderMessageAction()}
            {renderThemeAction()}
            {renderLanguageAction()}
          </div>
        </div>
      );
    }

    return (
      <div className={classes.option}>
        {renderAuthActions()}
        {renderDivider()}
        <div className={classes.actionGroup}>
          {renderDownloadAction()}
          {renderThemeAction()}
          {renderLanguageAction()}
        </div>
      </div>
    );
  };

  /* 平板/移动端紧凑操作区：含更多面板入口 */
  const renderCompactActions = () => {
    if (userinfo_loading) {
      return (
        <div className={classes.option}>
          <CircularProgress size={18} />
        </div>
      );
    }

    if (isPad) {
      return (
        <div className={classes.option}>
          {isLoggedIn ? renderUserAction() : renderAuthActions()}
          {renderDivider()}
          {renderMoreAction()}
          {renderLanguageAction()}
        </div>
      );
    }

    if (isLoggedIn) {
      return (
        <div className={classes.option}>
          {renderUserAction()}
          {renderDivider()}
          {renderMoreAction()}
        </div>
      );
    }

    const authActions = renderAuthActions({ showLogin: false });
    return (
      <div className={classes.option}>
        {authActions}
        {renderDivider()}
        {renderMoreAction()}
        {renderLanguageAction()}
      </div>
    );
  };

  /* ========== 最终渲染 ========== */
  return (
    <>
      <header className={classes.header}>
        <div className={classes.headerLeft}>
          {renderLogo()}
          {renderMainNav()}
        </div>
        {isDesktop ? renderDesktopActions() : renderCompactActions()}
      </header>
      <div
        className={cx(
          classes.mobileMask,
          moreOpen ? classes.mobileMaskOpen : "",
        )}
        onClick={() => setMoreOpen(false)}
      />
      <div
        className={cx(
          classes.mobilePanel,
          moreOpen ? classes.mobilePanelOpen : "",
        )}
      >
        {renderMoreContent()}
      </div>
    </>
  );
}

/*
 * HeaderRC 组件总结：
 * - 支持 3 种响应式断点：Desktop(>=1024) / Pad(768-1023) / Mobile(<768)
 * - 支持 3 种用户状态：未登录 / 已登录 / 子站模式(voox_co)
 * - 导航数据来自远程接口(index_v5)，不可用时回退到本地 fallbackMenuList
 * - 通过 renderHoverPopover 统一管理所有 hover 弹窗
 * - 更多面板(more)桌面端用 hover 触发，移动端用侧滑面板 + mask
 */
export default HeaderRC;
